/**
 * Configuración → «Saldo y costos»: saldo, gasto del mes, recargas, ajustes auditados, recargo,
 * tope, aviso de saldo bajo y los movimientos.
 *
 * El saldo es en DÓLARES (Twilio cobra en USD), y sólo en dólares: desde el 26/10/2026 no hay tipo de cambio ni
 * equivalente en pesos (decisión de Alan).
 *
 * Ajustar el saldo pide motivo SIEMPRE (D-C) y queda registrado con el email del operador: es la
 * única forma de mover plata sin una recarga con comprobante, y sin rastro sería imposible
 * reconstruir por qué el saldo no cierra con la factura de Twilio.
 *
 * Si el saldo no se pudo traer, se dice: nunca se muestra un «USD 0,00» que parezca real.
 *
 * «Saldo en Twilio» (la plata que Twilio le cobra a Deenex) y «Alertas de gasto de Twilio» (Usage
 * Triggers) ya no van acá: son de Twilio, no de la marca, y viven en «Conexión (técnico)»
 * (`SeccionCuenta`). Arriba de todo de esta pestaña estaba el saldo de Twilio, y una marca veía dos
 * saldos distintos sin saber cuál era el suyo.
 *
 * Una persona de un cliente (Accesos, rol «comercio») ve el saldo, el gasto y los movimientos; la cuenta
 * de Twilio (es de Deenex), las recargas, los ajustes y los costos (el recargo de Deenex incluido) no:
 * el backend se los niega (403 SOLO_EQUIPO) y acá no se muestran. De los movimientos tampoco ve quién del
 * equipo los registró (la columna «Registró», con su mail): el backend no le manda `registradoPor`.
 *
 * Con el servidor en twilio, los movimientos del simulado no se mezclan con los reales: se piden sin
 * ellos (`simulados=excluir`) y «Ver pruebas del simulado» los trae marcados «fuera del saldo». El
 * aviso del rebase dice adónde fue la plata de prueba cuando se pasó a real (si no, el saldo en 0
 * parecía plata perdida). En simulado, la tabla es la de siempre y no se manda el filtro.
 *
 * Los movimientos de Conversaciones (el cupo de servicio sin cargo de Meta, las respuestas y reaperturas desde la
 * bandeja) dicen su motivo en «Detalle» y llevan a la conversación. La columna leía sólo `categoria` y
 * `codigoError`: esas filas decían «—» y el operador veía devoluciones sin explicación.
 *
 * «Cargar saldo» recuerda que registrar la recarga NO carga plata en Twilio: hay que cargar lo mismo en su consola (o
 * prender la recarga automática). El 12/10/2026 HATSU tenía USD 59,36 cargados y Twilio USD 10,65. Si alcanza o no se
 * ve en Estadísticas → Detalle técnico → «Cuenta de Twilio».
 */

import { useCallback, useEffect, useId, useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, CheckCircle2, ChevronDown, ChevronLeft, ChevronRight, Info, Loader2, Plus, RefreshCw, Scale, Wallet } from "lucide-react";
import type { FiltroSimulados, MovimientoResultado, Saldo } from "../../types";
import { oficialApi } from "../../api";
import { useCuentaOficial } from "../../contexto";
import { validarAjuste, validarRecarga } from "../../logic/config";
import { leerError, leerRespuesta, textoError } from "../../logic/respuesta";
import { mesDelPeriodo, montoMovimiento, nombreDeQuien, usd } from "../../logic/formato";
import { TIPO_MOVIMIENTO } from "../../logic/textos";
import { textoRebase } from "../../logic/restos";
import { R_CONVERSACIONES } from "../../logic/conversaciones";
import { RECORDATORIO_RECARGA_TWILIO } from "../../logic/cobertura";
import { ModalMotivo } from "../../components/ModalMotivo";
import { Tarjeta } from "../../components/Tarjeta";
import StatTile from "../../../campana-nueva/components/StatTile";
import { fechaHora, num, pct } from "../../../campana-nueva/utils/format";
import { BotoneraGuardar, CampoNumero, useFormularioConfig } from "./SeccionEnvio";
import { useEsEquipoDeenex } from "../../../campana-nueva/hooks/useEsEquipoDeenex";

const POR_PAGINA = 50;
const R = "/activa/oficial";

/** `detalle.motivo` de un movimiento, dicho para el operador. Un motivo que no está acá no se muestra (es interno). */
const MOTIVO_MOVIMIENTO: Record<string, string> = {
  // se devuelve la parte de Meta: el envío se cobra igual (decir sólo «gratis» al lado de un cobro se contradecía)
  cupo_servicio: "Se devuelve el cargo de Meta: entró en los mensajes de servicio sin cargo del mes",
  respuesta_manual: "Respuesta desde Conversaciones",
  respuesta_manual_fallida: "La respuesta no salió",
  reapertura: "Plantilla desde Conversaciones",
  reapertura_fallida: "La plantilla no salió",
};
/** Sólo un ObjectId es una conversación: el reintegro del cupo manda "" si la respuesta todavía no estaba atada. */
const esIdConversacion = (x: unknown): x is string => typeof x === "string" && /^[a-f0-9]{24}$/i.test(x);

export default function SeccionSaldo() {
  const { modo, recargar: recargarCuenta } = useCuentaOficial();
  const equipo = useEsEquipoDeenex();
  const [pagina, setPagina] = useState(1);
  const [saldo, setSaldo] = useState<Saldo | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState("");
  const [verSimulados, setVerSimulados] = useState(false);
  const enTwilio = modo === "twilio";
  // Sólo en twilio: en simulado lo simulado es la demo y el backend no filtra nada.
  const simulados: FiltroSimulados | undefined = enTwilio ? (verSimulados ? "incluir" : "excluir") : undefined;

  const cargar = useCallback(async (p: number) => {
    setCargando(true);
    try {
      const l = leerRespuesta<Saldo>(await oficialApi.saldo(p, POR_PAGINA, simulados));
      if (!l.ok) { setError(textoError(l)); return; }
      if (!l.data) { setError("El servidor no devolvió el saldo."); return; }
      setSaldo(l.data);
      setError("");
    } catch (e) {
      setError(textoError(leerError(e)));
    } finally {
      setCargando(false);
    }
  }, [simulados]);

  useEffect(() => { void cargar(pagina); }, [cargar, pagina]);

  const cambiarVerSimulados = (v: boolean) => {
    setVerSimulados(v);
    setPagina(1);
  };
  const rebase = enTwilio ? textoRebase(saldo?.rebase) : null;

  /** Después de una recarga o un ajuste: la tabla vuelve a la primera página y el contexto se actualiza. */
  const alMover = async () => {
    if (pagina !== 1) setPagina(1); else await cargar(1);
    await recargarCuenta();
  };


  return (
    <div className="space-y-5">
      {!saldo && cargando ? (
        <div className="grid sm:grid-cols-3 gap-3" aria-busy="true">{[0, 1, 2].map((i) => <div key={i} className="h-32 rounded-2xl bg-slate-100 animate-pulse" />)}</div>
      ) : !saldo ? (
        <Tarjeta>
          <p className="text-sm font-semibold text-red-700">No pudimos traer el saldo.</p>
          <p className="text-sm text-slate-600 mt-1">{error}</p>
          <button type="button" onClick={() => void cargar(pagina)} className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-violet-700 hover:underline"><RefreshCw size={14} /> Reintentar</button>
        </Tarjeta>
      ) : (
        <div className="grid sm:grid-cols-3 gap-3">
          {rebase && (
            <p className="sm:col-span-3 flex items-start gap-2 rounded-xl border border-slate-200 bg-slate-50 px-4 py-2.5 text-sm text-slate-700">
              <Info size={15} className="flex-shrink-0 mt-0.5 text-slate-400" /> {rebase}
            </p>
          )}
          <StatTile icon={<Wallet size={15} />} label="Saldo" value={usd(saldo.saldoUSD)}
            tono={saldo.alertas?.includes("saldo_bajo") ? "baja" : "resultado"}
            sub={`${Number(saldo.saldoUSD) <= 0 ? "Sin saldo: cargá para poder enviar" : saldo.alertas?.includes("saldo_bajo") ? "Queda poco saldo" : "Disponible para enviar"}`}
            chip={modo === "simulated" ? { texto: "simulado", cls: "bg-amber-50 text-amber-700" } : undefined} />
          <StatTile icon={<CalendarClock size={15} />} label="Gastado este mes" value={usd(saldo.gastoMes?.usd)}
            sub={`${mesDelPeriodo(saldo.gastoMes?.mes)}${saldo.topeMensualUSD !== null ? ` · de ${usd(saldo.topeMensualUSD)}` : ""}`}
            meterPct={saldo.topeMensualUSD ? Math.min(100, pct(saldo.gastoMes?.usd ?? 0, saldo.topeMensualUSD)) : undefined}
            tono={saldo.alertas?.includes("tope_alcanzado") ? "baja" : "neutro"} />
          <StatTile icon={<Scale size={15} />} label="Tope mensual" value={saldo.topeMensualUSD !== null ? usd(saldo.topeMensualUSD) : "Sin tope"}
            sub={saldo.alertas?.includes("tope_alcanzado") ? "Alcanzado: no sale nada más este mes" : saldo.alertas?.includes("tope_80") ? "Ya se usó más del 80 %" : equipo ? "Se cambia abajo, en Costos y límites" : "Lo fija el equipo de Deenex"} />
        </div>
      )}

      {equipo && (
        <>
          <div className="grid lg:grid-cols-2 gap-5 items-start">
            <FormRecarga simulado={modo === "simulated"} onHecho={alMover} />
            <FormAjuste onHecho={alMover} />
          </div>

          <FormCostos />
        </>
      )}

      <Tarjeta titulo="Movimientos" right={(
        <div className="flex items-center gap-3 flex-wrap">
          {enTwilio && (
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-600">
              <input type="checkbox" checked={verSimulados} onChange={(e) => cambiarVerSimulados(e.target.checked)}
                className="rounded border-slate-300 text-violet-600 focus:ring-violet-500" />
              Ver pruebas del simulado
            </label>
          )}
          {saldo && <span className="text-xs text-slate-400">{num(saldo.total)} en total</span>}
        </div>
      )}>
        {error && saldo && <p className="mb-3 text-xs text-red-600">No pudimos actualizar los movimientos: {error}</p>}
        {!saldo ? (
          <p className="text-sm text-slate-400">{cargando ? "Cargando…" : "No pudimos traer los movimientos."}</p>
        ) : !saldo.movimientos?.length ? (
          <p className="text-sm text-slate-400">
            {enTwilio && !verSimulados ? "Todavía no hay movimientos reales. Las pruebas del simulado se ven con «Ver pruebas del simulado»." : "Todavía no hay movimientos."}
          </p>
        ) : (
          <>
            {/* En el celular cada movimiento es una tarjeta apilada (tipo y monto arriba, fecha y campaña abajo, el detalle
                entero): como tabla, a 375 px el monto quedaba fuera de la caja, detrás de un scroll horizontal que nadie ve.
                Es la MISMA tabla (una sola vez en la página): desde `sm` vuelve a ser tabla. */}
            <div className="sm:overflow-x-auto">
              <table className="block w-full text-sm sm:table">
                <thead className="hidden sm:table-header-group">
                  <tr className="text-left">
                    {["Fecha", "Tipo", "Monto", "Campaña", "Detalle", ...(equipo ? ["Registró"] : [])].map((h) => (
                      <th key={h} className={`pb-2 pr-4 text-[10px] font-bold uppercase tracking-wider text-slate-400 ${h === "Monto" ? "text-right" : ""}`}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="block divide-y divide-slate-50 sm:table-row-group">
                  {saldo.movimientos.map((m) => {
                    const motivo = m.detalle?.motivo ? MOTIVO_MOVIMIENTO[m.detalle.motivo] ?? "" : "";
                    const detalle = [motivo, m.detalle?.categoria, m.detalle?.codigoError ? `error ${m.detalle.codigoError}` : "", m.comprobante, m.nota].filter(Boolean).join(" · ");
                    const conversacionId = esIdConversacion(m.detalle?.conversacionId) ? m.detalle.conversacionId : null;
                    const quien = nombreDeQuien(m.registradoPor?.email);
                    return (
                      <tr key={m.id} data-movimiento={m.id} className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-0.5 py-2.5 align-top sm:table-row sm:py-0">
                        <td className="order-3 text-xs text-slate-500 whitespace-nowrap sm:order-none sm:py-2 sm:pr-4 sm:text-sm sm:text-slate-600">{fechaHora(m.createdAt)}</td>
                        <td className="order-1 min-w-0 text-slate-800 sm:order-none sm:py-2 sm:pr-4 sm:whitespace-nowrap">
                          {TIPO_MOVIMIENTO[m.tipo] ?? m.tipo}
                          {m.simulado && <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">simulado</span>}
                          {m.excluidoDelSaldo === true && <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">fuera del saldo</span>}
                        </td>
                        <td data-monto className={`order-2 text-right tabular-nums whitespace-nowrap font-semibold sm:order-none sm:py-2 sm:pr-4 ${m.montoUSD >= 0 ? "text-emerald-700" : "text-slate-700"}`}>
                          {montoMovimiento(m)}
                        </td>
                        <td className={`order-4 text-right text-xs sm:order-none sm:py-2 sm:pr-4 sm:text-left sm:text-sm ${m.campanaId ? "" : "hidden sm:table-cell"}`}>
                          {m.campanaId ? <Link to={`${R}/campanas/${encodeURIComponent(m.campanaId)}`} className="text-violet-700 hover:underline">Ver<span className="sm:hidden"> campaña</span></Link> : <span className="text-slate-300">—</span>}
                        </td>
                        <td className={`order-5 col-span-2 text-xs text-slate-500 break-words sm:order-none sm:py-2 sm:pr-4 sm:max-w-[260px] ${detalle || conversacionId ? "" : "hidden sm:table-cell"}`}>
                          <span>{detalle || "—"}</span>
                          {conversacionId && (
                            <Link to={`${R_CONVERSACIONES}/${encodeURIComponent(conversacionId)}?filtro=todas`} className="block mt-0.5 text-violet-700 hover:underline">Ver conversación</Link>
                          )}
                        </td>
                        {/* Quién lo registró dicho como persona (`nombreDeQuien`): el email del token («alan vía hatsu-demo@operador.deenex») no es para el dueño.
                            A una persona de un cliente, ni eso: es alguien del equipo. */}
                        {equipo && (
                          // el rótulo va en el CSS (`before:`) y sólo en el celular, donde no hay encabezado de columna
                          <td className={`order-6 col-span-2 text-xs text-slate-500 before:content-['Registró:_'] sm:before:content-none sm:order-none sm:py-2 ${quien ? "" : "hidden sm:table-cell"}`}>
                            {quien || "—"}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <Paginador pagina={saldo.page || pagina} total={saldo.total} limite={saldo.limit || POR_PAGINA} cargando={cargando} onIr={setPagina} />
          </>
        )}
      </Tarjeta>
    </div>
  );
}

function Paginador({ pagina, total, limite, cargando, onIr }: { pagina: number; total: number; limite: number; cargando: boolean; onIr: (p: number) => void }) {
  const paginas = Math.max(1, Math.ceil((Number(total) || 0) / (limite || POR_PAGINA)));
  if (paginas <= 1) return null;
  return (
    <div className="mt-4 flex items-center justify-end gap-2 text-sm">
      <button type="button" onClick={() => onIr(pagina - 1)} disabled={pagina <= 1 || cargando} aria-label="Página anterior"
        className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center disabled:opacity-40"><ChevronLeft size={15} /></button>
      <span className="text-slate-600 tabular-nums">Página {num(pagina)} de {num(paginas)}</span>
      <button type="button" onClick={() => onIr(pagina + 1)} disabled={pagina >= paginas || cargando} aria-label="Página siguiente"
        className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center disabled:opacity-40"><ChevronRight size={15} /></button>
    </div>
  );
}

// ── Recarga ──────────────────────────────────────────────────────────────────────────────────

function FormRecarga({ simulado, onHecho }: { simulado: boolean; onHecho: () => Promise<void> }) {
  const [monto, setMonto] = useState<number | null>(null);
  const [comprobante, setComprobante] = useState("");
  const [nota, setNota] = useState("");
  const [intento, setIntento] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [ok, setOk] = useState("");
  const [error, setError] = useState("");
  const idComp = useId();
  const idNota = useId();

  const errores = validarRecarga({ montoUSD: monto ?? NaN, comprobante });

  const enviar = async (e: FormEvent) => {
    e.preventDefault();
    setIntento(true);
    setOk("");
    setError("");
    if (Object.keys(errores).length || ocupado || monto === null) return;
    setOcupado(true);
    try {
      const l = leerRespuesta<MovimientoResultado>(await oficialApi.recargar({ montoUSD: monto, comprobante: comprobante.trim(), ...(nota.trim() ? { nota: nota.trim() } : {}) }));
      if (!l.ok) { setError(textoError(l)); return; }
      // en real, la recarga es un asiento: la plata en Twilio la carga alguien a mano (ver RECORDATORIO_RECARGA_TWILIO)
      setOk(`Se cargaron ${usd(monto)}. Saldo: ${usd(l.data?.saldoUSD)}.${simulado ? "" : " Acordate de cargar lo mismo en Twilio."}`);
      setMonto(null);
      setComprobante("");
      setNota("");
      setIntento(false);
      await onHecho();
    } catch (err) {
      setError(textoError(leerError(err)));
    } finally {
      setOcupado(false);
    }
  };

  return (
    <Tarjeta titulo={simulado ? "Cargar saldo (simulado)" : "Cargar saldo"}>
      <form onSubmit={enviar} className="space-y-4">
        <CampoNumero label="Monto" value={monto} onChange={setMonto} nulable min={0} max={10000} step={0.01} sufijo="USD"
          error={intento ? errores.montoUSD : undefined} />
        <div>
          <label htmlFor={idComp} className="text-sm font-medium text-slate-700">Comprobante</label>
          <input id={idComp} value={comprobante} onChange={(e) => setComprobante(e.target.value.slice(0, 200))} placeholder="Transferencia 23/09 #4411"
            className={`mt-1 w-full rounded-xl border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500 ${intento && errores.comprobante ? "border-red-300" : "border-slate-200"}`} />
          {intento && errores.comprobante && <p className="text-xs text-red-600 mt-1">{errores.comprobante}</p>}
        </div>
        <div>
          <label htmlFor={idNota} className="text-sm font-medium text-slate-700">Nota <span className="text-slate-400 font-normal">(opcional)</span></label>
          <input id={idNota} value={nota} onChange={(e) => setNota(e.target.value.slice(0, 500))}
            className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
        </div>
        {!simulado && (
          <p data-recordatorio-twilio className="flex items-start gap-1.5 text-xs text-slate-500">
            <Info size={13} className="flex-shrink-0 mt-0.5 text-slate-400" /> {RECORDATORIO_RECARGA_TWILIO}
          </p>
        )}
        {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
        {ok && <p role="status" className="text-sm text-emerald-700 inline-flex items-center gap-1.5"><CheckCircle2 size={15} /> {ok}</p>}
        <button type="submit" disabled={ocupado}
          className="inline-flex items-center gap-1.5 rounded-xl bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-40">
          {ocupado ? <Loader2 size={15} className="animate-spin" /> : <Plus size={15} />} Cargar
        </button>
      </form>
    </Tarjeta>
  );
}

// ── Ajuste ───────────────────────────────────────────────────────────────────────────────────

/**
 * El monto se escribe en el formulario y el MOTIVO se pide en el modal de confirmación (1 a 500
 * caracteres, obligatorio): así no hay forma de confirmar un ajuste sin haber escrito por qué.
 */
function FormAjuste({ onHecho }: { onHecho: () => Promise<void> }) {
  const [abierto, setAbierto] = useState(false);
  const [monto, setMonto] = useState<number | null>(null);
  const [intento, setIntento] = useState(false);
  const [confirmando, setConfirmando] = useState(false);
  const [ocupado, setOcupado] = useState(false);
  const [ok, setOk] = useState("");
  const [error, setError] = useState("");

  // El motivo lo valida el modal; acá sólo el monto (se le pasa un motivo cualquiera a la validación).
  const errorMonto = validarAjuste({ montoUSD: monto ?? NaN, motivo: "-" }).montoUSD;

  const pedirConfirmacion = (e: FormEvent) => {
    e.preventDefault();
    setIntento(true);
    setOk("");
    setError("");
    if (errorMonto || monto === null) return;
    setConfirmando(true);
  };

  const ajustar = async (motivo: string) => {
    if (monto === null) return;
    const errores = validarAjuste({ montoUSD: monto, motivo });
    if (Object.keys(errores).length) { setError(Object.values(errores).join(" ")); setConfirmando(false); return; }
    setOcupado(true);
    try {
      const l = leerRespuesta<MovimientoResultado>(await oficialApi.ajustar({ montoUSD: monto, motivo }));
      if (!l.ok) { setError(textoError(l)); return; }
      setOk(`Ajuste registrado. Saldo: ${usd(l.data?.saldoUSD)}.`);
      setMonto(null);
      setIntento(false);
      setConfirmando(false);
      await onHecho();
    } catch (err) {
      setError(textoError(leerError(err)));
    } finally {
      setOcupado(false);
    }
  };

  return (
    <section className="rounded-2xl border border-slate-100 bg-white shadow-sm">
      <button type="button" onClick={() => setAbierto((v) => !v)} aria-expanded={abierto}
        className="w-full flex items-center gap-2 px-6 py-4 text-base font-semibold text-slate-900 tracking-tight">
        {abierto ? <ChevronDown size={16} /> : <ChevronRight size={16} />} Corregir el saldo
      </button>
      {abierto && (
        <form onSubmit={pedirConfirmacion} className="px-6 pb-6 space-y-4">
          <p className="text-sm text-slate-500">Para corregir una diferencia con la factura del proveedor de WhatsApp. Positivo suma, negativo resta. Queda registrado con tu email y el motivo.</p>
          <CampoNumero label="Monto" value={monto} onChange={setMonto} nulable min={-10000} max={10000} step={0.01} sufijo="USD"
            error={intento ? errorMonto : undefined} />
          {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
          {ok && <p role="status" className="text-sm text-emerald-700 inline-flex items-center gap-1.5"><CheckCircle2 size={15} /> {ok}</p>}
          <button type="submit" disabled={ocupado}
            className="inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-40">
            <Scale size={15} /> Ajustar
          </button>
        </form>
      )}
      {confirmando && monto !== null && (
        <ModalMotivo
          titulo="Ajustar el saldo"
          descripcion={`Vas a ${monto > 0 ? "sumar" : "restar"} ${usd(Math.abs(monto))}. Queda registrado con tu email.`}
          confirmar={monto > 0 ? "Sumar" : "Restar"}
          peligro={monto < 0}
          onConfirmar={(m) => void ajustar(m)}
          onCancelar={() => setConfirmando(false)}
          busy={ocupado}
        />
      )}
    </section>
  );
}

// ── Costos y límites (config) ────────────────────────────────────────────────────────────────

function FormCostos() {
  const f = useFormularioConfig();
  const e = f.editado;
  if (!e) return null;

  return (
    <Tarjeta titulo="Costos y límites">
      <div className="grid sm:grid-cols-3 gap-5">
        <CampoNumero label="Recargo de Deenex" value={e.recargoPct ?? null} onChange={(n) => f.set("recargoPct", n as number)} min={0} max={100} step={0.5} sufijo="%"
          error={f.errores.recargoPct} ayuda="Se suma a lo que cobran Meta y el proveedor de WhatsApp." />
        <CampoNumero label="Tope de gasto mensual" value={e.topeMensualUSD} onChange={(n) => f.set("topeMensualUSD", n)} nulable min={0} step={1} sufijo="USD"
          placeholder="Sin tope" error={f.errores.topeMensualUSD} ayuda="Vacío = sin tope." />
        <CampoNumero label="Avisar con saldo menor a" value={e.umbralSaldoBajoUSD} onChange={(n) => f.set("umbralSaldoBajoUSD", n as number)} min={0} step={1} sufijo="USD"
          error={f.errores.umbralSaldoBajoUSD} />
      </div>

      <p className="text-xs text-slate-400 mt-4">El saldo, los cobros y los costos son en dólares.</p>

      <div className="mt-4">
        <BotoneraGuardar f={f} />
      </div>
    </Tarjeta>
  );
}
